import * as React from "react";
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Search, Box } from "lucide-react";
import { toast } from "sonner";
import { ChatBlockedState } from "../../chat/components/molecules/ChatBlockedState";
import {
  TaskpanePanelShell,
  PanelSearchField,
  PanelEmptyState,
  PanelLoadingState,
} from "@/taskpane/shared/components/panel-layout";
import BlocksHeader from "./molecules/BlocksHeader";
import BlocksList from "./molecules/BlocksList";
import PlaceholderControl from "./molecules/PlaceholderControl";
import { useTemplateBlocks } from "../hooks/useTemplateBlocks";
import { useBlockPlaceholders } from "../hooks/useBlockPlaceholders";
import { useBlockPlaceholderNavigation } from "../hooks/useBlockPlaceholderNavigation";
import { TemplateBlock } from "../types/blockTypes";
import { BlocksService } from "../services/blocksService";
import { BlockInsertionService } from "../services/blockInsertionService";
import DocumentViewer from "../../knowledgebase/components/molecules/DocumentViewer";

interface BlocksPanelProps {
  executionId?: string;
  workpillId?: string;
  userId?: string;
  onOpenProjectSelector?: () => void;
}

const BlocksPanel: React.FC<BlocksPanelProps> = ({
  executionId,
  workpillId,
  userId: _userId,
  onOpenProjectSelector,
}) => {
  const { blocks, isLoading, error, templateId, refetch } = useTemplateBlocks(workpillId);
  const { placeholderCount, refreshPlaceholders, isRefreshing } = useBlockPlaceholders();
  const placeholderNav = useBlockPlaceholderNavigation();
  
  const [searchTerm, setSearchTerm] = useState("");
  const [previewBlock, setPreviewBlock] = useState<{ block: TemplateBlock; url: string } | null>(null);

  // Refresh placeholder count and warm-up when panel is shown
  useEffect(() => {
    // Warm-up del endpoint de organización
    BlocksService.warmUpOrganization();

    refreshPlaceholders();
    placeholderNav.refreshCount();
  }, [refreshPlaceholders, placeholderNav.refreshCount]);

  // Filtrar bloques según el término de búsqueda
  const filteredBlocks = React.useMemo(() => {
    if (!searchTerm.trim()) {
      return blocks;
    }

    const lowerSearch = searchTerm.toLowerCase();
    return blocks.filter(
      (block) =>
        block.title.toLowerCase().includes(lowerSearch) ||
        block.description?.toLowerCase().includes(lowerSearch)
    );
  }, [blocks, searchTerm]);

  const handleBlockPreview = async (block: TemplateBlock) => {
    try {
      toast.loading('Loading preview...');
      const url = await BlocksService.getBlockPreviewUrl(block);
      toast.dismiss();
      
      if (url) {
        setPreviewBlock({ block, url });
      } else {
        toast.error('Cannot generate preview for this block');
      }
    } catch (error) {
      toast.dismiss();
      toast.error('Error loading preview');
      console.error('Error loading block preview:', error);
    }
  };

  const handleInsertionSuccess = async () => {
    await refreshPlaceholders();
    await placeholderNav.refreshCount();
    await refetch();
  };

  const handleInsertAtCursor = async (block: TemplateBlock) => {
    if (!templateId || !workpillId || !executionId) {
      toast.error('Incomplete configuration');
      return;
    }

    const toastId = toast.loading('Inserting block...');

    try {
      // 1. Insertar {{bloque}} en la posición del cursor
      const insertResult = await BlockInsertionService.insertPlaceholderAtCursor();
      
      if (!insertResult.success) {
        throw new Error(insertResult.error || 'Error inserting placeholder');
      }

      // 2. Guardar documento en background
      const saved = await BlockInsertionService.saveDocumentInBackground(workpillId, executionId);
      if (!saved) {
        throw new Error('Could not save document before processing block');
      }

      // 3. Llamar al backend para procesar el bloque
      const processResult = await BlocksService.insertBlocks(
        templateId,
        workpillId,
        executionId,
        [{ block_id: block.id, order: insertResult.order.toString() }]
      );

      if (!processResult.success) {
        throw new Error(processResult.error || 'Error processing block');
      }

      // 4. Recargar el documento actualizado
      if (processResult.storage_path) {
        toast.loading('Loading updated document...', { id: toastId });
        await BlocksService.loadDocumentFromStorage(
          processResult.storage_path, 
          processResult.storage_bucket || 'execution-templates'
        );
      }

      toast.success(`Block "${block.title}" inserted successfully!`, { id: toastId });
      await handleInsertionSuccess();
      
    } catch (error) {
      console.error('Error inserting block:', error);
      toast.error(error instanceof Error ? error.message : 'Error inserting block', { id: toastId });
    }
  };

  const handleInsertAtPlaceholder = async (block: TemplateBlock, placeholderIndex: number) => {
    if (!templateId || !workpillId || !executionId) {
      toast.error('Incomplete configuration');
      return;
    }

    const toastId = toast.loading('Inserting block...');
    
    try {
      // 1. Guardar documento actual en background antes de procesar
      const saved = await BlockInsertionService.saveDocumentInBackground(workpillId, executionId);
      if (!saved) {
        throw new Error('Could not save document before processing block');
      }

      // 2. Llamar al backend para procesar el bloque
      const processResult = await BlocksService.insertBlocks(
        templateId,
        workpillId,
        executionId,
        [{ block_id: block.id, order: placeholderIndex.toString() }]
      );

      if (!processResult.success) {
        throw new Error(processResult.error || 'Error processing block');
      }

      // 3. Recargar el documento actualizado
      if (processResult.storage_path) {
        toast.loading('Loading updated document...', { id: toastId });
        await BlocksService.loadDocumentFromStorage(
          processResult.storage_path, 
          processResult.storage_bucket || 'execution-templates'
        );
      }

      toast.success(`Block "${block.title}" inserted at position ${placeholderIndex}!`, { id: toastId });
      await handleInsertionSuccess();
      
    } catch (error) {
      console.error('Error inserting block:', error);
      toast.error(error instanceof Error ? error.message : 'Error inserting block', { id: toastId });
    }
  };

  if (!executionId && onOpenProjectSelector) {
    return (
      <TaskpanePanelShell variant="flex" className="items-center justify-center !p-4">
        <ChatBlockedState onOpenSelector={onOpenProjectSelector} />
      </TaskpanePanelShell>
    );
  }

  if (isLoading) {
    return (
      <TaskpanePanelShell>
        <PanelLoadingState message="Loading blocks…" />
      </TaskpanePanelShell>
    );
  }

  if (error) {
    return (
      <TaskpanePanelShell>
        <div className="flex flex-col items-center justify-center gap-3 p-12 text-red-600">
          <AlertTriangle className="h-8 w-8 text-red-500" />
          <p className="m-0 text-center text-xs text-slate-600">{error}</p>
          <Button onClick={() => refetch()} className="mt-2" size="sm" variant="outline">
            Retry
          </Button>
        </div>
      </TaskpanePanelShell>
    );
  }

  if (blocks.length === 0) {
    return (
      <TaskpanePanelShell>
        <BlocksHeader blocksCount={blocks.length} />
        <div className="mb-4">
          <PlaceholderControl
            count={placeholderNav.totalCount}
            currentIndex={placeholderNav.currentIndex}
            onRefresh={async () => {
              await refreshPlaceholders();
              await placeholderNav.refreshCount();
            }}
            isRefreshing={isRefreshing}
            onNavigateNext={placeholderNav.navigateNext}
            onNavigatePrevious={placeholderNav.navigatePrevious}
          />
        </div>
        <PanelEmptyState
          icon={Box}
          title="No blocks available"
          description="No blocks are defined for this template."
        />
      </TaskpanePanelShell>
    );
  }

  // Si hay vista previa activa, mostrar el viewer
  if (previewBlock) {
    return (
      <div className="flex-1 overflow-hidden bg-white">
        <DocumentViewer
          document={{
            id: previewBlock.block.id,
            name: previewBlock.block.title,
            description: previewBlock.block.description || undefined,
            url: previewBlock.url,
            storage_path: previewBlock.block.content,
            created_at: previewBlock.block.created_at,
            status: 'completed',
            knowledge_base_id: previewBlock.block.template_id,
            knowledge_base_name: 'Template Block',
            type: 'file'
          }}
          onClose={() => setPreviewBlock(null)}
        />
      </div>
    );
  }

  return (
    <TaskpanePanelShell>
      <BlocksHeader blocksCount={blocks.length} />

      <div className="mb-4 grid grid-cols-1 gap-2 sm:grid-cols-3 sm:items-center">
        <div className="sm:col-span-2">
          <PanelSearchField
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Search blocks…"
            className="w-full"
          />
        </div>
        <div className="flex justify-end sm:col-span-1">
          <PlaceholderControl
            count={placeholderNav.totalCount}
            currentIndex={placeholderNav.currentIndex}
            onRefresh={async () => {
              await refreshPlaceholders();
              await placeholderNav.refreshCount();
            }}
            isRefreshing={isRefreshing}
            onNavigateNext={placeholderNav.navigateNext}
            onNavigatePrevious={placeholderNav.navigatePrevious}
          />
        </div>
      </div>

      {filteredBlocks.length === 0 && searchTerm ? (
        <PanelEmptyState
          icon={Search}
          title={`No blocks match “${searchTerm}”`}
          description="Try a different search."
          compact
        />
      ) : (
        <BlocksList
          blocks={filteredBlocks}
          placeholderCount={placeholderCount}
          currentPlaceholderIndex={placeholderNav.currentIndex}
          onInsertAtCursor={handleInsertAtCursor}
          onInsertAtPlaceholder={handleInsertAtPlaceholder}
          onBlockPreview={handleBlockPreview}
        />
      )}
    </TaskpanePanelShell>
  );
};

export default BlocksPanel;

